/**
 * @puffin-core/auth-client — transport adapters.
 *
 * The factory (`createAuthService`) is transport-agnostic: it calls a single
 * injected `http.request({ method, path, body, auth, signal, query })` that
 * resolves `{ ok, status, body, networkError? }` and NEVER throws on an HTTP
 * status (only on caller abort). These builders produce that adapter.
 *
 * `body` is the **parsed backend JSON in on-the-wire shape** (`{ success, data,
 * error }` for enveloped routes, or the un-enveloped object for the few routes
 * that don't envelope). The factory performs per-operation extraction
 * (`body.data ?? body`, etc.) — so the adapter's only envelope job is to deliver
 * the wire body unchanged.
 *
 * `auth` ∈ `'none'` (pre-auth, token-less public client) | `'storage'` (Bearer
 * from the authed client's `getToken`, with 401-refresh) | `{ token }` (explicit
 * one-off Bearer on the public client — device flows whose token did NOT come
 * from authStorage).
 *
 * @module @puffin-core/auth-client/transports
 */

import { createFetchSeam } from '@puffin-core/api-client/fetch-seam';

function buildUrl(path, query) {
  if (!query) return path;
  const qs = new URLSearchParams(query).toString();
  return qs ? `${path}?${qs}` : path;
}

function isExplicitToken(auth) {
  return Boolean(auth) && typeof auth === 'object' && typeof auth.token === 'string';
}

/**
 * Build a transport over a pair of `@puffin-core/api-client` instances.
 *
 * Both clients MUST share the auth base URL (e.g. `/api/v1/auth`) so the
 * factory's base-relative paths resolve. The authed client carries
 * `getToken`/`onTokenRefresh` (so authenticated auth ops get 401-refresh); the
 * public client carries neither (clean pre-auth calls, no stale-token attach).
 *
 * @param {Object} deps
 * @param {import('axios').AxiosInstance} deps.publicClient  - createApiClient (no getToken)
 * @param {import('axios').AxiosInstance} deps.authedClient  - createApiClient (getToken + refresh)
 * @returns {(req: Object) => Promise<{ok: boolean, status: number, body: any, networkError?: boolean}>}
 */
export function createApiClientTransport({ publicClient, authedClient }) {
  if (!publicClient || !authedClient) {
    throw new TypeError('createApiClientTransport requires { publicClient, authedClient }');
  }

  return async function request({ method, path, body, auth = 'none', signal, query }) {
    const explicitToken = isExplicitToken(auth);
    // Explicit one-off tokens + pre-auth calls go through the token-less public
    // client; 'storage' goes through the authed client (token attach + refresh).
    const client = auth === 'storage' ? authedClient : publicClient;
    const headers = {};
    if (explicitToken) {
      headers.Authorization = `Bearer ${auth.token}`;
    }

    try {
      const res = await client.request({
        url: buildUrl(path, query),
        method,
        data: body,
        headers,
        signal,
        // The auth base paths are not in the client's publicPaths allow-list;
        // the factory gates token presence itself (mustHaveToken), so skip the
        // ABG-001 pre-flight and let the backend answer.
        authRequired: false,
      });
      return { ok: true, status: res.status, body: res.data ?? null };
    } catch (err) {
      // createApiClient normalizes non-2xx to a plain `{ status, message, data,
      // code }` rejection and converges aborts to a DOMException('AbortError').
      if (err && err.name === 'AbortError') {
        throw err;
      }
      const status = typeof err?.status === 'number' ? err.status : 0;
      return { ok: false, status, body: err?.data ?? null, networkError: status === 0 };
    }
  };
}

/**
 * Build a transport over the native `fetch` API + an injected token store.
 *
 * Faithful to the legacy per-app raw-`fetch` semantics (no per-call 401-refresh;
 * Bearer pulled from the token store on 'storage'). Useful for apps not yet on a
 * dedicated `@puffin-core/api-client` auth pair, and for tests.
 *
 * @param {Object} deps
 * @param {string} deps.base - Auth base URL prefix (e.g. '/api/v1/auth')
 * @param {{getAccessToken: () => (string|null)}} [deps.tokenStore]
 * @param {typeof fetch} [deps.fetchImpl] - Injectable for tests; defaults to the shared fetch seam
 *   (`createFetchSeam({ label: 'createFetchTransport' })`), whose absent-global rejection this transport answers
 *   as a `{networkError:true}` like any failure to reach the server (SEAM-001 B2c, lead ruling R2).
 * @param {boolean} [deps.withCredentials=false] - Send cookies (credentials: 'include')
 * @param {number} [deps.timeoutMs=30000] - Default request timeout (parity with the
 *   api-client transport's 30s timeout). A hung request fails fast as a retryable
 *   `{networkError:true}`; a caller-supplied signal abort still propagates as AbortError.
 *   Pass `0` to disable the built-in timeout (rely solely on the caller's signal).
 * @returns {(req: Object) => Promise<{ok: boolean, status: number, body: any, networkError?: boolean}>}
 */
export function createFetchTransport({
  base,
  tokenStore,
  fetchImpl,
  withCredentials = false,
  timeoutMs = 30000,
}) {
  if (typeof base !== 'string') {
    throw new TypeError('createFetchTransport requires a string { base }');
  }
  const doFetch = fetchImpl || createFetchSeam({ label: 'createFetchTransport' });
  // Parity with createApiClientTransport (which inherits the @puffin-core/api-client
  // 30s default timeout): a hung auth request fails fast as a retryable network error
  // instead of spinning indefinitely. A caller-supplied AbortSignal still wins — its
  // abort propagates as AbortError (a user cancel), distinct from our timeout.
  const canTimeout = typeof AbortController === 'function' && timeoutMs > 0;

  return async function request({ method, path, body, auth = 'none', signal, query }) {
    const headers = { 'Content-Type': 'application/json' };
    if (auth === 'storage' && tokenStore) {
      const token = tokenStore.getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } else if (isExplicitToken(auth)) {
      headers.Authorization = `Bearer ${auth.token}`;
    }

    let controller = null;
    let timer = null;
    let timedOut = false;
    let callerAborted = false;
    let onCallerAbort = null;
    let effectiveSignal = signal;

    if (canTimeout) {
      controller = new AbortController();
      effectiveSignal = controller.signal;
      if (signal) {
        if (signal.aborted) {
          callerAborted = true;
          controller.abort();
        } else {
          onCallerAbort = () => {
            callerAborted = true;
            controller.abort();
          };
          signal.addEventListener('abort', onCallerAbort, { once: true });
        }
      }
      timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeoutMs);
    }

    const init = { method, headers, signal: effectiveSignal };
    if (body !== undefined) init.body = JSON.stringify(body);
    if (withCredentials) init.credentials = 'include';

    let response;
    try {
      response = await doFetch(`${base}${buildUrl(path, query)}`, init);
    } catch (err) {
      // Our timeout fired (not a caller cancel) → retryable network error.
      if (timedOut && !callerAborted) {
        return { ok: false, status: 0, body: null, networkError: true };
      }
      if (err && err.name === 'AbortError') throw err;
      return { ok: false, status: 0, body: null, networkError: true };
    } finally {
      if (timer) clearTimeout(timer);
      if (signal && onCallerAbort) signal.removeEventListener('abort', onCallerAbort);
    }

    let parsed;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }
    return { ok: response.ok, status: response.status, body: parsed };
  };
}
